import React, { useState, useEffect } from "react";
import { fetchContent, updateContent } from "../../services/api";
import { useContent } from "../../context/ContentContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { useToast } from "../../hooks/use-toast";
import ImageUploader from "./ImageUploader";
import { Loader2, Plus, X, Save } from "lucide-react";

const AdminPages = () => {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { refresh } = useContent();

  useEffect(() => { fetchContent().then(setC).catch(() => {}); }, []);

  const setF = (field, v) => setC((p) => ({ ...p, factoryPage: { ...p.factoryPage, [field]: v } }));
  const setFArr = (field, i, key, v) => setF(field, c.factoryPage[field].map((it, idx) => (idx === i ? { ...it, [key]: v } : it)));
  const setFImg = (i, v) => setF("aboutImages", c.factoryPage.aboutImages.map((it, idx) => (idx === i ? v : it)));
  const setFCert = (i, v) => setF("certifications", c.factoryPage.certifications.map((it, idx) => (idx === i ? v : it)));

  const setG = (field, v) => setC((p) => ({ ...p, galleryPage: { ...p.galleryPage, [field]: v } }));
  const setGItem = (i, key, v) => setG("items", c.galleryPage.items.map((it, idx) => (idx === i ? { ...it, [key]: v } : it)));
  const addGItem = () => setG("items", [...(c.galleryPage.items || []), { image: "", category: "", title: "" }]);
  const removeGItem = (i) => setG("items", c.galleryPage.items.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await updateContent({ factoryPage: c.factoryPage, galleryPage: c.galleryPage });
      await refresh();
      toast({ title: "Pages saved" });
    } catch (e) { toast({ title: "Save failed", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  if (!c) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 text-[#C9A227] animate-spin" /></div>;
  const f = c.factoryPage; const g = c.galleryPage;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display font-bold text-lg text-[#14171c]">Factory & Gallery Pages</h2>
        <Button onClick={save} disabled={saving} className="bg-[#C9A227] hover:bg-[#A6841B] text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save
        </Button>
      </div>

      <Tabs defaultValue="factory">
        <TabsList className="mb-5 bg-white border border-neutral-200">
          <TabsTrigger value="factory" className="data-[state=active]:bg-[#C9A227] data-[state=active]:text-white">Factory Page</TabsTrigger>
          <TabsTrigger value="gallery" className="data-[state=active]:bg-[#C9A227] data-[state=active]:text-white">Gallery Page</TabsTrigger>
        </TabsList>

        <TabsContent value="factory" className="space-y-6">
          <div className="bg-white rounded-xl border border-neutral-200 p-6 space-y-3">
            <h3 className="font-display font-bold text-[#14171c]">Hero</h3>
            <ImageUploader label="Hero Background Image" value={f.heroImage} onChange={(v) => setF("heroImage", v)} />
            <div><Label className="text-xs">Eyebrow</Label><Input className="mt-1" value={f.heroEyebrow} onChange={(e) => setF("heroEyebrow", e.target.value)} /></div>
            <div><Label className="text-xs">Title</Label><Input className="mt-1" value={f.heroTitle} onChange={(e) => setF("heroTitle", e.target.value)} /></div>
            <div><Label className="text-xs">Subtitle</Label><Textarea className="mt-1" rows={2} value={f.heroSubtitle} onChange={(e) => setF("heroSubtitle", e.target.value)} /></div>
          </div>

          <div className="bg-white rounded-xl border border-neutral-200 p-6 space-y-3">
            <h3 className="font-display font-bold text-[#14171c]">About Section</h3>
            <div><Label className="text-xs">Eyebrow</Label><Input className="mt-1" value={f.aboutEyebrow} onChange={(e) => setF("aboutEyebrow", e.target.value)} /></div>
            <div><Label className="text-xs">Title</Label><Input className="mt-1" value={f.aboutTitle} onChange={(e) => setF("aboutTitle", e.target.value)} /></div>
            <div><Label className="text-xs">Paragraph 1</Label><Textarea className="mt-1" rows={3} value={f.aboutText1} onChange={(e) => setF("aboutText1", e.target.value)} /></div>
            <div><Label className="text-xs">Paragraph 2</Label><Textarea className="mt-1" rows={3} value={f.aboutText2} onChange={(e) => setF("aboutText2", e.target.value)} /></div>
            <Label className="text-xs">Images (4)</Label>
            <div className="grid grid-cols-2 gap-3">
              {(f.aboutImages || []).map((img, i) => (<ImageUploader key={i} label="" value={img} onChange={(v) => setFImg(i, v)} />))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-[#14171c]">Production Process</h3>
              <button type="button" onClick={() => setF("process", [...f.process, { step: "", title: "", desc: "" }])} className="inline-flex items-center gap-1 text-sm font-semibold text-[#C9A227]"><Plus className="w-4 h-4" /> Add step</button>
            </div>
            <div className="space-y-2">
              {(f.process || []).map((p, i) => (
                <div key={i} className="grid grid-cols-[60px_1fr] gap-2 items-start border border-neutral-100 rounded-lg p-2 relative">
                  <Input value={p.step} onChange={(e) => setFArr("process", i, "step", e.target.value)} placeholder="01" />
                  <div className="space-y-2 pr-6">
                    <Input value={p.title} onChange={(e) => setFArr("process", i, "title", e.target.value)} placeholder="Title" />
                    <Textarea rows={2} value={p.desc} onChange={(e) => setFArr("process", i, "desc", e.target.value)} placeholder="Description" />
                  </div>
                  <button type="button" onClick={() => setF("process", f.process.filter((_, idx) => idx !== i))} className="absolute top-2 right-2 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-neutral-200 p-6">
            <h3 className="font-display font-bold text-[#14171c] mb-3">Quality Control Cards</h3>
            <div className="space-y-2">
              {(f.qualityControl || []).map((q, i) => (
                <div key={i} className="space-y-2 border border-neutral-100 rounded-lg p-3">
                  <Input value={q.title} onChange={(e) => setFArr("qualityControl", i, "title", e.target.value)} placeholder="Title" />
                  <Textarea rows={2} value={q.desc} onChange={(e) => setFArr("qualityControl", i, "desc", e.target.value)} placeholder="Description" />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-[#14171c]">Certifications</h3>
              <button type="button" onClick={() => setF("certifications", [...f.certifications, ""])} className="inline-flex items-center gap-1 text-sm font-semibold text-[#C9A227]"><Plus className="w-4 h-4" /> Add</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {(f.certifications || []).map((cert, i) => (
                <div key={i} className="flex gap-1">
                  <Input value={cert} onChange={(e) => setFCert(i, e.target.value)} />
                  <button type="button" onClick={() => setF("certifications", f.certifications.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 px-1"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="gallery" className="space-y-6">
          <div className="bg-white rounded-xl border border-neutral-200 p-6 space-y-3">
            <h3 className="font-display font-bold text-[#14171c]">Gallery Heading</h3>
            <div><Label className="text-xs">Eyebrow</Label><Input className="mt-1" value={g.eyebrow} onChange={(e) => setG("eyebrow", e.target.value)} /></div>
            <div><Label className="text-xs">Title</Label><Input className="mt-1" value={g.title} onChange={(e) => setG("title", e.target.value)} /></div>
            <div><Label className="text-xs">Subtitle</Label><Textarea className="mt-1" rows={2} value={g.subtitle} onChange={(e) => setG("subtitle", e.target.value)} /></div>
          </div>

          <div className="bg-white rounded-xl border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-[#14171c]">Gallery Items</h3>
              <button type="button" onClick={addGItem} className="inline-flex items-center gap-1 text-sm font-semibold text-[#C9A227]"><Plus className="w-4 h-4" /> Add image</button>
            </div>
            <div className="space-y-4">
              {(g.items || []).map((item, i) => (
                <div key={i} className="border border-neutral-200 rounded-lg p-4 relative">
                  <button type="button" onClick={() => removeGItem(i)} className="absolute top-3 right-3 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                  <ImageUploader label="Image" value={item.image} onChange={(v) => setGItem(i, "image", v)} />
                  <div className="mt-3 grid md:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Category / Filter</Label><Input className="mt-1" value={item.category} onChange={(e) => setGItem(i, "category", e.target.value)} /></div>
                    <div><Label className="text-xs">Title</Label><Input className="mt-1" value={item.title} onChange={(e) => setGItem(i, "title", e.target.value)} /></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Button onClick={save} disabled={saving} className="mt-6 bg-[#C9A227] hover:bg-[#A6841B] text-white">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Pages
      </Button>
    </div>
  );
};

export default AdminPages;
